import { http } from '@/utils/api'
import { withPbx } from '@/stores/network/api'
import * as Sentry from '@/core/sentry'
import qs from 'qs'
import type { ColorName } from '@/utils/colors'
import { isWhitelistError } from '@/utils/api/whitelist'
import type { Locale } from '@/utils/locale'
import { loadJob, type JobResponse } from '../job/api'

export type TtsLanguage = Exclude<Locale, 'debug'>

export type TtsMetadata = {
  original_text: string
  voice: 'carmelo' | 'frederic' | 'nichalia' | 'enniah'
  language: TtsLanguage
  generated_at?: string
}

export type AudioFile = {
  id: number
  type: 'message' | 'voice_mail' | 'music_on_hold' | 'waiting_music'
  category: 'prompt' | 'music_on_hold' | 'waiting_music'
  color?: null | ColorName
  pbxId: number
  name: string
  assigned: boolean | AssignedExtensionsType
  public: boolean
  canEdit: boolean
  canSetPrivate: boolean
  tts_metadata?: TtsMetadata
  source?: 'recording' | 'upload' | 'tts' | null
}

export type AssignedExtensionsType = {
  extendedPromptForwardings?: number[]
  promptForwardings?: number[]
  extendedForwardingProfiles?: number[]
  cfc?: number[]
}

export async function loadPrompt(type: AudioFile['type'], id: number | string) {
  try {
    const url = withPbx(`audio-files/${id}`)

    const { data } = await http.get<AudioFile>(url, {
      params: {
        type,
      },
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function createPrompt(prompt: FormData) {
  try {
    const url = withPbx('audio-files')

    const { data } = await http.post<AudioFile>(url, prompt, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updatePrompt(
  type: AudioFile['type'],
  id: string | number,
  inputData: FormData
) {
  try {
    inputData.append('_method', 'PUT')

    const url = withPbx(`audio-files/${id}`)

    const { data } = await http.post(url, inputData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        type,
      },
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error: any) {
    Sentry.captureException(error)

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function assignMusicOnHold(
  type: AudioFile['type'],
  id: string | number,
  input: { assigned: boolean }
) {
  try {
    const url = withPbx(`audio-files/${id}`)

    const { data } = await http.put<AudioFile>(url, input, {
      params: {
        type,
      },
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deletePrompt(
  type: AudioFile['type'],
  id: string | number
) {
  try {
    const url = withPbx(`audio-files/${id}`)

    const { data } = await http.delete(url, { params: { type } })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function loadAudioFile(
  type: AudioFile['type'],
  id: string | number
) {
  try {
    const url = withPbx(`audio-files/${id}`)

    const { data } = await http.get(url, {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'audio/wav',
      },
      params: {
        type,
        download_audio: true,
        t: Date.now(),
      },
      paramsSerializer: (params) => {
        return qs.stringify(params)
      },
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

type JobCreationResponse = {
  job_id: number
  status_url: string
}

export async function generateTts(
  {
    id,
    type,
    name,
    text,
    language,
    voice,
  }: {
    id: AudioFile['id'] | undefined
    type: AudioFile['type']
    name?: AudioFile['name']
    text: TtsMetadata['original_text']
    language: TtsMetadata['language']
    voice: TtsMetadata['voice']
  },
  onStatusChange?: (status: JobResponse['status']) => void
) {
  try {
    const url = withPbx('audio-files/text-to-speech')

    const payload = {
      text,
      name,
      type,
      voice,
      language,
      ...(id
        ? {
            audio_file_id: id,
          }
        : undefined),
    }

    const { data } = await http.post<JobCreationResponse>(url, payload)

    const result = await loadJob<AudioFile>(data.status_url, onStatusChange)

    if (result.status === 'success') {
      return result
    }

    const ttsError = new Error('TTS generation failed') as Error & {
      jobResult?: typeof result
    }
    ttsError.jobResult = result
    throw ttsError
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export type TtsLastSettings = {
  language: TtsMetadata['language']
  voice: TtsMetadata['voice']
  blocked?: boolean
  usage?: number
}

export async function loadLastTtsSettings() {
  try {
    const url = withPbx('audio-files/text-to-speech/last-settings')

    const { data } = await http.get<TtsLastSettings>(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    return {
      status: 'error' as const,
      payload: error,
    }
  }
}
